import { useState, useEffect, useCallback } from 'react';
import * as projectService from '../../../services/projectService';
import { Project } from '../../../types/project';
import { formatRelativeTimeWithPrefix } from '../../../utils/formatters';

/**
 * Hook para gerenciar lista de projetos
 *
 * Features:
 * - Lista de projetos do usuário
 * - Busca por nome
 * - CRUD operations
 * - Ordenação por última atualização
 *
 * @returns Estado e funções para gerenciar projetos
 */
export const useProjects = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  /**
   * Fetch projects from API
   */
  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await projectService.getUserProjects(100, 0);

      if (response.success && Array.isArray(response.data)) {
        // Sort by updated_at (most recent first)
        const sortedProjects = response.data.sort((a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        );
        setProjects(sortedProjects);
      }
    } catch (err) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Erro ao carregar projetos';
      setError(errorMessage);
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Refresh projects
   */
  const refresh = useCallback(() => {
    fetchProjects();
  }, [fetchProjects]);

  /**
   * Create a new project
   */
  const createProject = useCallback(async (data: { name: string; description?: string }) => {
    try {
      const response = await projectService.createProject(data);
      if (response.success && response.data) {
        setProjects(prev => [response.data!, ...prev]);
        return response.data;
      }
      throw new Error('Failed to create project');
    } catch (err) {
      console.error('Error creating project:', err);
      throw err;
    }
  }, []);

  /**
   * Update a project
   */
  const updateProject = useCallback(async (id: string, data: { name?: string; description?: string }) => {
    try {
      const response = await projectService.updateProject(id, data);
      if (response.success && response.data) {
        setProjects(prev =>
          prev.map(p => p.id === id ? { ...p, ...response.data } : p)
        );
        return response.data;
      }
      throw new Error('Failed to update project');
    } catch (err) {
      console.error('Error updating project:', err);
      throw err;
    }
  }, []);

  /**
   * Delete a project
   */
  const deleteProject = useCallback(async (id: string) => {
    try {
      await projectService.deleteProject(id);
      setProjects(prev => prev.filter(p => p.id !== id));
      return true;
    } catch (err) {
      console.error('Error deleting project:', err);
      throw err;
    }
  }, []);

  /**
   * Filter projects by search query
   */
  const filteredProjects = projects.filter(project => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      project.name.toLowerCase().includes(query) ||
      project.description?.toLowerCase().includes(query)
    );
  });

  // Initial fetch
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return {
    projects: filteredProjects,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    refresh,
    createProject,
    updateProject,
    deleteProject,
    formatRelativeTime: formatRelativeTimeWithPrefix,
  };
};
